import React, { useEffect, useRef, useState } from 'react';
import { getScrollParent } from '../../AIEmployees/useReveal';
import './LinearHowItWorks.css';

/**
 * LinearHowItWorks — §4.4 of the /linear page.
 *
 * Three static AI-generated step images, each paired with title + description.
 * Simulator stays interactive (§4.2); these three tell the story visually.
 */
const STEPS = [
  {
    num: '01',
    title: 'Assign',
    body: (
      <>
        Assign any Linear issue to <code>@CellCog</code>, or @mention it in a
        comment. No special syntax. No separate interface.
      </>
    ),
    thumbnail: '/assets/linear/step-1-assign.webp',
    alt: 'An obsidian sphere being directed toward a larger sculptural form in purple atmospheric haze with a teal arc connecting them',
    placeholderLabel: '@CellCog assignment',
  },
  {
    num: '02',
    title: 'Watch',
    body: (
      <>
        CellCog thinks out loud. Follow along in Linear&rsquo;s agent session
        panel. Ask follow-up questions in comments. CellCog asks clarifying
        questions when it needs them.
      </>
    ),
    thumbnail: '/assets/linear/step-2-watch.webp',
    alt: 'A sculptural glass helix with internal teal glow and motion particles in atmospheric purple mist, suggesting work unfolding in real time',
    placeholderLabel: 'Live agent session',
  },
  {
    num: '03',
    title: 'Ship',
    body: (
      <>
        When CellCog completes, you get a response activity with clickable
        artifacts — documents, videos, apps, dashboards — all viewable by your
        Linear teammates with their Linear email.
      </>
    ),
    thumbnail: '/assets/linear/step-3-ship.webp',
    alt: 'A central luminous teal ring with refined product forms radiating around it on a dark reflective surface, representing completed deliverables',
    placeholderLabel: 'Response with artifacts',
  },
];

const StepBand = ({ step, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { root: getScrollParent(ref.current), threshold: 0, rootMargin: '0px 0px 100% 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const isImageLeft = index % 2 === 0;

  return (
    <div
      className={`step-band reveal ${visible ? 'is-visible' : ''} ${
        isImageLeft ? 'step-band--img-left' : 'step-band--img-right'
      }`}
      ref={ref}
    >
      <div className="step-band__media">
        <div className="step-band__media-frame">
          {!imgError ? (
            <img
              src={step.thumbnail}
              alt={step.alt}
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="step-band__placeholder">
              <div className="step-band__placeholder-chrome">
                <span className="dot red" />
                <span className="dot yellow" />
                <span className="dot green" />
              </div>
              <div className="step-band__placeholder-body">
                <span>{step.placeholderLabel}</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="step-band__content">
        <span className="step-band__num display-font">{step.num}</span>
        <h3 className="step-band__title display-font">{step.title}</h3>
        <p className="step-band__body">{step.body}</p>
      </div>
    </div>
  );
};

const LinearHowItWorks = () => (
  <section id="linear-how-it-works" className="linear-how-it-works section">
    <div className="linear-how-it-works__header">
      <h2 className="section-title display-font">The flow is just Linear.</h2>
      <p className="section-subtitle">
        No new interface to learn. No context switching. Delegation happens
        inside the tool your team already uses.
      </p>
    </div>

    <div className="linear-how-it-works__steps">
      {STEPS.map((step, i) => (
        <StepBand key={step.num} step={step} index={i} />
      ))}
    </div>
  </section>
);

export default LinearHowItWorks;
